import React, { useId } from 'react';

interface SparklineProps {
  /** Oldest value first. Fewer than two points renders nothing. */
  points: number[];
  className?: string;
  /** Drawn in `currentColor`, so the parent decides positive/negative hue. */
  strokeWidth?: number;
}

/**
 * A 60x20 trend line. Purely decorative: the number and its delta carry the
 * meaning, so it is hidden from assistive tech rather than described badly.
 */
const Sparkline: React.FC<SparklineProps> = ({ points, className = 'h-5 w-16', strokeWidth = 1.5 }) => {
  const gradientId = useId();
  if (points.length < 2) return null;

  const width = 60;
  const height = 20;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = width / (points.length - 1);

  const coords = points.map((value, index) => {
    const x = index * step;
    // Inset by the stroke so the extremes are not clipped at the edges.
    const y = height - strokeWidth - ((value - min) / span) * (height - strokeWidth * 2);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${coords.join(' ')} ${width},${height}`} fill={`url(#${gradientId})`} />
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

export default Sparkline;
